import React, { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { useAuth } from "../AuthContext";
import {
  DRAFT_QUOTE_BY_ID_QUERY,
  DRAFT_QUOTE_FINALIZATION_PREVIEW_QUERY,
  ADD_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_SHIRRED_VALANCE_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_SHIRRED_VALANCE_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_BOX_SPRING_COVER_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_BOX_SPRING_COVER_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_AUSTRIAN_SHADE_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_AUSTRIAN_SHADE_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_BALLOON_SHADE_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_BALLOON_SHADE_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_DUVET_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_DUVET_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_COVERLET_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_COVERLET_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_TABLECLOTH_ROUND_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_TABLECLOTH_ROUND_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_TABLECLOTH_RECTANGLE_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_TABLECLOTH_RECTANGLE_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_BENCH_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_BENCH_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_CORNICE_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_CORNICE_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_BED_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_BED_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_REUPHOLSTER_BENCH_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_REUPHOLSTER_BENCH_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_REUPHOLSTER_OTTOMAN_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_REUPHOLSTER_OTTOMAN_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_REUPHOLSTER_POP_SEAT_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_REUPHOLSTER_POP_SEAT_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_REUPHOLSTER_DINING_CHAIR_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_REUPHOLSTER_DINING_CHAIR_DRAFT_QUOTE_OBJECT_MUTATION,
  ADD_PIN_BOARD_DRAFT_QUOTE_OBJECT_MUTATION,
  UPDATE_PIN_BOARD_DRAFT_QUOTE_OBJECT_MUTATION,
  ARCHIVE_DRAFT_QUOTE_OBJECT_MUTATION,
  ARCHIVE_DRAFT_QUOTE_OBJECT_IMAGE_MUTATION,
  CREATE_DRAFT_QUOTE_OBJECT_ITEM_MUTATION,
  UPDATE_DRAFT_QUOTE_OBJECT_ITEM_MUTATION,
  UPDATE_DRAFT_QUOTE_OBJECT_ITEM_PRODUCT_TYPE_MUTATION,
  UPDATE_DRAFT_QUOTE_MUTATION,
  FINALIZE_QUOTE_MUTATION,
  RESYNC_FINALIZED_QUOTE_MUTATION,
} from "../graphql/draftQuotes";
import { CREATE_ADDRESS_MUTATION } from "../graphql/customers";
import AddressPicker, {
  AddressOption,
  AddressPickerHandle,
} from "../components/Address/AddressPicker";
import {
  ItemTypeEnum,
  WINDOW_TYPES,
  SOFT_GOODS_TYPES,
  FURNITURE_TYPES,
  BENCH_TYPES,
  CORNICE_TYPES,
  BED_TYPES,
  REUPHOLSTER_BENCH_TYPES,
  REUPHOLSTER_OTTOMAN_TYPES,
  REUPHOLSTER_POP_SEAT_TYPES,
  REUPHOLSTER_DINING_CHAIR_TYPES,
  PIN_BOARD_TYPES,
  ALL_TYPES,
  ITEM_TYPE_FORM_COMPONENTS,
  ObjectTypeFormHandle,
} from "../components/DraftQuoteObjects";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArchiveIcon from "@mui/icons-material/Archive";
import UndoIcon from "@mui/icons-material/Undo";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import "./QuoteDetail.css";

const SERVER_URL = process.env.REACT_APP_API_HOST || "http://localhost:8080";

// Sham is intentionally NOT in this set — it uses its own fixed-size
// concept (Standard/Euro/Queen/King, sham_size column), not a real bed size,
// even though two of its labels happen to overlap with bed_size's values.
// See objectSummaryLine's dedicated sham branch below.
const BED_ITEMS_SET = new Set<ItemTypeEnum>([
  "dustskirt",
  "box_spring_cover",
  "duvet",
  "coverlet",
]);

// QuickBooks-facing product classification (issue #51) — independent of the
// section a line happens to be grouped under (e.g. "labor" mixes roller
// shade fabrication with every other item type's). Classified automatically
// at object-creation time (server/src/plugins/draftQuoteObjectCalc.ts) but
// freely editable here, same PRODUCT_TYPE_OPTIONS list QuoteDetail.tsx uses
// for the finalized-quote equivalent of this field.
const PRODUCT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "FABRIC", label: "Fabric" },
  { value: "VELCRO", label: "Velcro" },
  { value: "LINING", label: "Lining" },
  { value: "TRIM", label: "Trim" },
  { value: "ROLLEASE", label: "Rollease" },
  { value: "HARDWARE", label: "Hardware" },
  { value: "MOTORIZATION", label: "Motorization" },
  { value: "SHADE", label: "Shade" },
  { value: "INSTALLATION", label: "Installation" },
  { value: "MEASURE", label: "Measure" },
  { value: "OTHER", label: "Other" },
  { value: "B_SHADES", label: "B Shades" },
  { value: "CUSTOM", label: "Custom" },
  { value: "FURNITURE", label: "Furniture" },
];

interface DraftItem {
  id: number;
  section: string;
  productType: string | null;
  description: string;
  quantity: string | number;
  unitPrice: string | number;
  notes: string | null;
  formulaNote: string | null;
  archivedAt: string | null;
}

interface DraftObjectImage {
  id: number;
  storedFilename: string;
  originalFilename: string;
  createdAt: string;
  archivedAt: string | null;
}

interface DraftObject {
  id: number;
  name: string | null;
  sortOrder: number;
  quantity: number;
  widthInches: string | null;
  heightInches: string | null;
  depthInches: string | null;
  fabricDescription: string | null;
  liningType: string | null;
  bumpInterlining: boolean;
  operatingSystem: string | null;
  hardwareType: string | null;
  itemType: ItemTypeEnum | null;
  mountType: "inside" | "outside" | null;
  rollDirection: string | null;
  driveSide: string | null;
  chainColor: string | null;
  bedSize: string | null;
  weltType: string | null;
  contrastWeltFabricDescription: string | null;
  insertType: string | null;
  dustskirtStyle: string | null;
  spaceAboveWindowInches: string | null;
  trimPlacement: string | null;
  trimDescription: string | null;
  fabricWidthInches: string | null;
  liningWidthInches: string | null;
  flapSizeInches: string | null;
  patternRepeatInches: string | null;
  liningDescription: string | null;
  fasciaSize: number | null;
  mechanism: string | null;
  inventoryItemId: number | null;
  interliningType: string | null;
  fabricRepeatInches: string | null;
  rodFaceInches: string | null;
  overlapInches: string | null;
  returnInches: string | null;
  hemSize: string | null;
  pleatType: string | null;
  ruffle: boolean;
  shamSize: string | null;
  dropInches: string | null;
  duvetInsert: boolean;
  seatHeightInches: string | null;
  seatDepthInches: string | null;
  armHeightInches: string | null;
  backHeightInches: string | null;
  legHeightInches: string | null;
  cushionThicknessInches: string | null;
  armThicknessInches: string | null;
  seatDeckThicknessInches: string | null;
  backCushionThicknessInches: string | null;
  legProfile: string | null;
  furnitureType: string | null;
  customWoodFascia: boolean;
  customWoodFasciaPaintColor: string | null;
  benchType: string | null;
  benchShape: string | null;
  benchStyle: string | null;
  storageOption: boolean;
  channelSize: string | null;
  pillowTopSize: string | null;
  legSize: string | null;
  legStyle: string | null;
  legFinish: string | null;
  legPriceCents: number | null;
  trimTop: boolean;
  trimBottom: boolean;
  nailheadTop: boolean;
  nailheadBottom: boolean;
  nailheadColor: string | null;
  corniceShape: string | null;
  corniceOverProduct: boolean;
  nailheadSides: boolean;
  nailheadFace: boolean;
  weltTop: boolean;
  weltBottom: boolean;
  weltSides: boolean;
  weltFace: boolean;
  bedConfig: string | null;
  headboardStyle: string | null;
  headboardShaped: boolean;
  bedEdgeTreatment: string | null;
  trimSides: boolean;
  trimFace: boolean;
  mattressLengthInches: string | null;
  mattressWidthInches: string | null;
  frameThicknessInches: string | null;
  sideRailHeightInches: string | null;
  footboardHeightInches: string | null;
  sideRailStyleDescription: string | null;
  footboardStyleDescription: string | null;
  sideRailFabricDescription: string | null;
  bedDrawersSmallQty: number | null;
  bedDrawersLargeQty: number | null;
  bedDrawersSmallDetails: string[] | null;
  bedDrawersLargeDetails: string[] | null;
  bedTrundle: boolean;
  bedFeetType: string | null;
  bedFeetHeightInches: string | null;
  bedFeetFinish: string | null;
  bedFeetSpecialtyDescription: string | null;
  bedFeetSpecialtyPriceCents: number | null;
  bedBunkyBoards: boolean;
  bedBunkyBoardWidthInches: string | null;
  includeMeasureCharge: boolean;
  reupholsterBenchStyle: string | null;
  baseballStitching: boolean;
  buttons: boolean;
  buttonCount: number | null;
  reupholsterSkirtType: string | null;
  ottomanShape: string | null;
  tufted: boolean;
  attachedPillowTop: boolean;
  hrFoamDacron: boolean;
  diningChairConfig: string | null;
  backWidthInches: string | null;
  seatFinish: string | null;
  seatWeltPlacement: string | null;
  bandingSize: string | null;
  seatNailheadColor: string | null;
  insideBackFinish: string | null;
  insideBackNailheadColor: string | null;
  outsideBackFinish: string | null;
  outsideBackNailheadColor: string | null;
  skirt: boolean;
  skirtDropInches: string | null;
  touchUpWood: boolean;
  refinishOrPaint: boolean;
  pinBoardFrame: boolean;
  pinBoardFramePaintColor: string | null;
  pinBoardRibbonNailheads: boolean;
  pinBoardMounting: string | null;
  pinBoardMeasureTemplate: boolean;
  notes: string | null;
  archivedAt: string | null;
  draftQuoteObjectItemsByDraftQuoteObjectId: { nodes: DraftItem[] };
  draftQuoteObjectImagesByDraftQuoteObjectId: { nodes: DraftObjectImage[] };
}

interface DraftQuoteData {
  id: number;
  name: string;
  notes: string | null;
  createdAt: string;
  finalizedQuoteId: number | null;
  jobByJobId: {
    id: number;
    name: string;
    addressByAddressId: AddressOption | null;
    customerByCustomerId: {
      id: number;
      name: string;
      addressesByCustomerId: { nodes: AddressOption[] };
    };
  };
  addressByBillToAddressId: AddressOption | null;
  addressByShipToAddressId: AddressOption | null;
  draftQuoteObjectsByDraftQuoteId: { nodes: DraftObject[] };
}

function dedupeAddresses(addresses: AddressOption[]): AddressOption[] {
  const seen = new Set<number>();
  return addresses.filter((a) => {
    if (seen.has(a.id)) return false;
    seen.add(a.id);
    return true;
  });
}

// 'shade' / 'installation' are roller-shade-specific (see rollerShadeCalculations.ts);
// 'motorization' is used by traversing drape/side panel/roman shade (their
// own dedicated quote-line section, split out of the generic 'hardware'
// bucket); every other item type still uses the original 5.
const SECTION_ORDER = [
  "shade",
  "fabric",
  "labor",
  "hardware",
  "motorization",
  "furniture",
  "installation",
  "other",
] as const;

const SECTION_COLORS: Record<string, { bg: string; text: string }> = {
  shade: { bg: "#dbeafe", text: "#1e40af" },
  fabric: { bg: "#dbeafe", text: "#1e40af" },
  labor: { bg: "#ede9fe", text: "#5b21b6" },
  hardware: { bg: "#fef3c7", text: "#92400e" },
  motorization: { bg: "#fef3c7", text: "#92400e" },
  furniture: { bg: "#d1fae5", text: "#065f46" },
  installation: { bg: "#ede9fe", text: "#5b21b6" },
  other: { bg: "#f3f4f6", text: "#374151" },
};

// Display override for the 'motorization' section badge — many operating
// systems (Cord & Cleat, Wand, Clutch) aren't actually motorized, so the
// section is shown to users as "Operating System" while the underlying
// section key/data model stays 'motorization'.
const SECTION_LABELS: Partial<Record<(typeof SECTION_ORDER)[number], string>> =
  {
    motorization: "Operating System",
  };

function formatPrice(cents: string | number): string {
  const n = Number(cents);
  if (n === 0) return "—";
  return `$${(n / 100).toFixed(2)}`;
}

function formatQty(qty: string | number, section: string): string {
  const n = Number(qty);
  const formatted = n % 1 === 0 ? String(n) : n.toFixed(2);
  return section === "fabric" ? `${formatted} yds` : formatted;
}

// Matches FurnitureForm.tsx's FURNITURE_STYLE_OPTIONS slugs.
const FURNITURE_TYPE_LABELS: Record<string, string> = {
  loveseat: "Loveseat",
  sofa_couch: "Sofa / Couch",
  sectional: "Sectional / L-Couch",
};

function objectSummaryLine(obj: DraftObject): string {
  const parts: string[] = [];
  if (obj.quantity > 1) parts.push(`×${obj.quantity}`);
  if (obj.itemType === "furniture" && obj.furnitureType) {
    parts.push(FURNITURE_TYPE_LABELS[obj.furnitureType] || obj.furnitureType);
  } else {
    const typeLabel = ALL_TYPES.find((t) => t.value === obj.itemType)?.label;
    if (typeLabel) parts.push(typeLabel);
  }

  if (obj.itemType === "sham") {
    if (obj.shamSize)
      parts.push(obj.shamSize.charAt(0).toUpperCase() + obj.shamSize.slice(1));
  } else if (obj.itemType === "bed") {
    if (obj.bedConfig)
      parts.push(obj.bedConfig === "entire_bed" ? "Entire Bed" : "Headboard Only");
    if (obj.headboardStyle)
      parts.push(
        obj.headboardStyle.charAt(0).toUpperCase() + obj.headboardStyle.slice(1),
      );
    if (obj.widthInches && obj.heightInches) {
      parts.push(`${Number(obj.widthInches)}" × ${Number(obj.heightInches)}"`);
    }
  } else if (obj.itemType === "cornice") {
    // width_inches is the WINDOW width; show the derived cornice width
    // (OM = window + 2", IM = window − ¼") so the summary matches what's
    // actually built and priced.
    if (obj.widthInches && obj.heightInches) {
      const windowWidth = Number(obj.widthInches);
      const corniceWidth =
        obj.mountType === "inside" ? windowWidth - 0.25 : windowWidth + 2;
      parts.push(`${corniceWidth}" × ${Number(obj.heightInches)}"`);
    }
    if (obj.mountType) parts.push(obj.mountType === "inside" ? "IM" : "OM");
    if (obj.corniceShape === "shaped") parts.push("Shaped bottom");
  } else if (obj.itemType === "reupholster_ottoman") {
    // Generic Width × Height would drop Depth (and a Round ottoman's
    // Diameter is stored as width), so spell out the real dimensions.
    if (obj.widthInches) {
      const w = Number(obj.widthInches);
      const h = obj.heightInches ? ` × ${Number(obj.heightInches)}" H` : "";
      if (obj.ottomanShape === "round") {
        parts.push(`${w}" Diameter${h}`);
      } else if (obj.depthInches) {
        parts.push(`${w}" W × ${Number(obj.depthInches)}" D${h}`);
      } else {
        parts.push(`${w}" W${h}`);
      }
    }
  } else if (obj.itemType === "pin_board") {
    if (obj.widthInches && obj.heightInches) {
      parts.push(`${Number(obj.widthInches)}" W × ${Number(obj.heightInches)}" L`);
    }
    if (obj.pinBoardFrame) parts.push("Frame");
    if (obj.pinBoardRibbonNailheads) parts.push("Ribbon + nailheads");
    if (obj.pinBoardMounting === "velcro") parts.push("Velcro");
    if (obj.pinBoardMounting === "french_cleat") parts.push("French cleat");
    if (obj.pinBoardMeasureTemplate) parts.push("Measure & template");
  } else if (obj.itemType && BED_ITEMS_SET.has(obj.itemType)) {
    if (obj.bedSize)
      parts.push(obj.bedSize.charAt(0).toUpperCase() + obj.bedSize.slice(1));
    if (obj.dustskirtStyle) parts.push(obj.dustskirtStyle.replace("_", " "));
  } else {
    if (obj.widthInches && obj.heightInches) {
      parts.push(`${Number(obj.widthInches)}" × ${Number(obj.heightInches)}"`);
    } else if (obj.widthInches) {
      parts.push(`${Number(obj.widthInches)}" W`);
    }
    if (obj.mountType) {
      parts.push(obj.mountType === "inside" ? "IM" : "OM");
    }
  }

  if (obj.liningType && obj.liningType !== "standard") {
    parts.push(
      obj.liningType.charAt(0).toUpperCase() +
        obj.liningType.slice(1) +
        " lining",
    );
  }
  if (obj.bumpInterlining) parts.push("Bump");
  if (obj.interliningType && obj.interliningType !== "none") {
    parts.push(
      obj.interliningType.charAt(0).toUpperCase() +
        obj.interliningType.slice(1) +
        " interlining",
    );
  }
  if (obj.weltType && obj.weltType !== "none") {
    parts.push(obj.weltType === "self" ? "Self welt" : "Contrast welt");
  }
  if (obj.ruffle) parts.push("Ruffle");
  if (obj.rollDirection)
    parts.push(
      obj.rollDirection === "regular" ? "Regular roll" : "Reverse roll",
    );
  if (obj.driveSide) parts.push(obj.driveSide.toUpperCase());
  if (obj.chainColor) parts.push(obj.chainColor);
  if (obj.operatingSystem && obj.operatingSystem !== "manual") {
    parts.push(obj.operatingSystem === "somfy_standard" ? "Somfy" : "Rollease");
  }
  if (obj.fabricDescription) parts.push(obj.fabricDescription);
  return parts.join(" · ");
}

const blankObjectForm = {
  name: "",
  quantity: "1",
  itemType: "" as ItemTypeEnum | "",
  notes: "",
};

// Raw inputs a type-specific form's getValues() can return — used to rehydrate
// a form via "Go back" from a previously saved object's stored columns.
function buildInitialValuesFromObject(obj: DraftObject): Record<string, any> {
  return {
    widthInches: obj.widthInches,
    heightInches: obj.heightInches,
    depthInches: obj.depthInches,
    fabricDescription: obj.fabricDescription,
    liningType: obj.liningType,
    bumpInterlining: obj.bumpInterlining,
    operatingSystem: obj.operatingSystem,
    hardwareType: obj.hardwareType,
    mountType: obj.mountType,
    rollDirection: obj.rollDirection,
    driveSide: obj.driveSide,
    chainColor: obj.chainColor,
    bedSize: obj.bedSize,
    weltType: obj.weltType,
    contrastWeltFabricDescription: obj.contrastWeltFabricDescription,
    insertType: obj.insertType,
    dustskirtStyle: obj.dustskirtStyle,
    spaceAboveWindowInches: obj.spaceAboveWindowInches,
    trimPlacement: obj.trimPlacement,
    trimDescription: obj.trimDescription,
    fabricWidthInches: obj.fabricWidthInches,
    liningWidthInches: obj.liningWidthInches,
    flapSizeInches: obj.flapSizeInches,
    patternRepeatInches: obj.patternRepeatInches,
    fasciaSize: obj.fasciaSize,
    mechanism: obj.mechanism,
    inventoryItemId: obj.inventoryItemId,
    interliningType: obj.interliningType,
    fabricRepeatInches: obj.fabricRepeatInches,
    rodFaceInches: obj.rodFaceInches,
    overlapInches: obj.overlapInches,
    returnInches: obj.returnInches,
    hemSize: obj.hemSize,
    pleatType: obj.pleatType,
    ruffle: obj.ruffle,
    shamSize: obj.shamSize,
    dropInches: obj.dropInches,
    duvetInsert: obj.duvetInsert,
    seatHeightInches: obj.seatHeightInches,
    seatDepthInches: obj.seatDepthInches,
    armHeightInches: obj.armHeightInches,
    backHeightInches: obj.backHeightInches,
    legHeightInches: obj.legHeightInches,
    cushionThicknessInches: obj.cushionThicknessInches,
    armThicknessInches: obj.armThicknessInches,
    seatDeckThicknessInches: obj.seatDeckThicknessInches,
    backCushionThicknessInches: obj.backCushionThicknessInches,
    legProfile: obj.legProfile,
    furnitureType: obj.furnitureType,
    customWoodFascia: obj.customWoodFascia,
    customWoodFasciaPaintColor: obj.customWoodFasciaPaintColor,
    benchType: obj.benchType,
    benchShape: obj.benchShape,
    benchStyle: obj.benchStyle,
    storageOption: obj.storageOption,
    channelSize: obj.channelSize,
    pillowTopSize: obj.pillowTopSize,
    legSize: obj.legSize,
    legStyle: obj.legStyle,
    legFinish: obj.legFinish,
    legPriceCents: obj.legPriceCents,
    trimTop: obj.trimTop,
    trimBottom: obj.trimBottom,
    nailheadTop: obj.nailheadTop,
    nailheadBottom: obj.nailheadBottom,
    nailheadColor: obj.nailheadColor,
    corniceShape: obj.corniceShape,
    corniceOverProduct: obj.corniceOverProduct,
    nailheadSides: obj.nailheadSides,
    nailheadFace: obj.nailheadFace,
    weltTop: obj.weltTop,
    weltBottom: obj.weltBottom,
    weltSides: obj.weltSides,
    weltFace: obj.weltFace,
    bedConfig: obj.bedConfig,
    headboardStyle: obj.headboardStyle,
    headboardShaped: obj.headboardShaped,
    bedEdgeTreatment: obj.bedEdgeTreatment,
    trimSides: obj.trimSides,
    trimFace: obj.trimFace,
    mattressLengthInches: obj.mattressLengthInches,
    mattressWidthInches: obj.mattressWidthInches,
    frameThicknessInches: obj.frameThicknessInches,
    sideRailHeightInches: obj.sideRailHeightInches,
    footboardHeightInches: obj.footboardHeightInches,
    sideRailStyleDescription: obj.sideRailStyleDescription,
    footboardStyleDescription: obj.footboardStyleDescription,
    sideRailFabricDescription: obj.sideRailFabricDescription,
    bedDrawersSmallQty: obj.bedDrawersSmallQty,
    bedDrawersLargeQty: obj.bedDrawersLargeQty,
    bedDrawersSmallDetails: obj.bedDrawersSmallDetails,
    bedDrawersLargeDetails: obj.bedDrawersLargeDetails,
    bedTrundle: obj.bedTrundle,
    bedFeetType: obj.bedFeetType,
    bedFeetHeightInches: obj.bedFeetHeightInches,
    bedFeetFinish: obj.bedFeetFinish,
    bedFeetSpecialtyDescription: obj.bedFeetSpecialtyDescription,
    bedFeetSpecialtyPriceCents: obj.bedFeetSpecialtyPriceCents,
    bedBunkyBoards: obj.bedBunkyBoards,
    bedBunkyBoardWidthInches: obj.bedBunkyBoardWidthInches,
    includeMeasureCharge: obj.includeMeasureCharge,
    reupholsterBenchStyle: obj.reupholsterBenchStyle,
    baseballStitching: obj.baseballStitching,
    buttons: obj.buttons,
    buttonCount: obj.buttonCount,
    reupholsterSkirtType: obj.reupholsterSkirtType,
    ottomanShape: obj.ottomanShape,
    tufted: obj.tufted,
    attachedPillowTop: obj.attachedPillowTop,
    hrFoamDacron: obj.hrFoamDacron,
    diningChairConfig: obj.diningChairConfig,
    backWidthInches: obj.backWidthInches,
    seatFinish: obj.seatFinish,
    seatWeltPlacement: obj.seatWeltPlacement,
    bandingSize: obj.bandingSize,
    seatNailheadColor: obj.seatNailheadColor,
    insideBackFinish: obj.insideBackFinish,
    insideBackNailheadColor: obj.insideBackNailheadColor,
    outsideBackFinish: obj.outsideBackFinish,
    outsideBackNailheadColor: obj.outsideBackNailheadColor,
    skirt: obj.skirt,
    skirtDropInches: obj.skirtDropInches,
    touchUpWood: obj.touchUpWood,
    refinishOrPaint: obj.refinishOrPaint,
    pinBoardFrame: obj.pinBoardFrame,
    pinBoardFramePaintColor: obj.pinBoardFramePaintColor,
    pinBoardRibbonNailheads: obj.pinBoardRibbonNailheads,
    pinBoardMounting: obj.pinBoardMounting,
    pinBoardMeasureTemplate: obj.pinBoardMeasureTemplate,
  };
}

// Default variables for the 5 item types whose add/update goes through
// dedicated mutations (see handleAddObject) — keyed by ItemTypeEnum, each
// entry's `defaults` lists every arg that mutation accepts besides
// draftQuoteId/name/quantity/sortOrder/objectId, spread before typeFields so
// the form's own getValues() output overrides these null placeholders.
const DEDICATED_TYPE_CONFIG: Partial<
  Record<ItemTypeEnum, { defaults: Record<string, any> }>
> = {
  box_spring_cover: {
    defaults: {
      bedSize: null,
      widthInches: null,
      heightInches: null,
      depthInches: null,
      fabricDescription: null,
      inventoryItemId: null,
      liningDescription: null,
    },
  },
  duvet: {
    defaults: {
      bedSize: null,
      weltType: null,
      duvetInsert: null,
      fabricDescription: null,
      inventoryItemId: null,
      liningDescription: null,
    },
  },
  coverlet: {
    defaults: {
      bedSize: null,
      fabricDescription: null,
      inventoryItemId: null,
      liningDescription: null,
    },
  },
  tablecloth_round: {
    defaults: {
      widthInches: null,
      heightInches: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      fabricDescription: null,
      inventoryItemId: null,
      liningDescription: null,
    },
  },
  tablecloth_rectangle: {
    defaults: {
      widthInches: null,
      heightInches: null,
      depthInches: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      fabricDescription: null,
      inventoryItemId: null,
      liningDescription: null,
    },
  },
  austrian_shade: {
    defaults: {
      widthInches: null,
      heightInches: null,
      mountType: null,
      spaceAboveWindowInches: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      fabricDescription: null,
      inventoryItemId: null,
      liningType: null,
      liningDescription: null,
      bumpInterlining: null,
      trimPlacement: null,
      operatingSystem: null,
    },
  },
  balloon_shade: {
    defaults: {
      widthInches: null,
      heightInches: null,
      mountType: null,
      spaceAboveWindowInches: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      fabricDescription: null,
      inventoryItemId: null,
      liningType: null,
      liningDescription: null,
      bumpInterlining: null,
      trimPlacement: null,
      operatingSystem: null,
    },
  },
  bench: {
    defaults: {
      benchType: null,
      benchShape: null,
      benchStyle: null,
      widthInches: null,
      heightInches: null,
      depthInches: null,
      storageOption: null,
      channelSize: null,
      pillowTopSize: null,
      fabricDescription: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      inventoryItemId: null,
      legSize: null,
      legStyle: null,
      legFinish: null,
      legPriceCents: null,
      trimTop: null,
      trimBottom: null,
      trimDescription: null,
      nailheadTop: null,
      nailheadBottom: null,
      nailheadColor: null,
    },
  },
  cornice: {
    defaults: {
      mountType: null,
      widthInches: null,
      heightInches: null,
      corniceShape: null,
      corniceOverProduct: null,
      fabricDescription: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      inventoryItemId: null,
      trimTop: null,
      trimBottom: null,
      trimSides: null,
      trimFace: null,
      trimDescription: null,
      weltTop: null,
      weltBottom: null,
      weltSides: null,
      weltFace: null,
      nailheadTop: null,
      nailheadBottom: null,
      nailheadSides: null,
      nailheadFace: null,
      nailheadColor: null,
    },
  },
  bed: {
    defaults: {
      bedConfig: null,
      headboardStyle: null,
      headboardShaped: null,
      widthInches: null,
      heightInches: null,
      fabricDescription: null,
      inventoryItemId: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      bedEdgeTreatment: null,
      trimTop: null,
      trimSides: null,
      trimFace: null,
      mattressLengthInches: null,
      mattressWidthInches: null,
      frameThicknessInches: null,
      sideRailHeightInches: null,
      footboardHeightInches: null,
      sideRailStyleDescription: null,
      footboardStyleDescription: null,
      sideRailFabricDescription: null,
      bedDrawersSmallQty: null,
      bedDrawersLargeQty: null,
      bedDrawersSmallDetails: null,
      bedDrawersLargeDetails: null,
      bedTrundle: null,
      bedFeetType: null,
      bedFeetHeightInches: null,
      bedFeetFinish: null,
      bedFeetSpecialtyDescription: null,
      bedFeetSpecialtyPriceCents: null,
      bedBunkyBoards: null,
      bedBunkyBoardWidthInches: null,
      includeMeasureCharge: null,
    },
  },
  reupholster_bench: {
    defaults: {
      reupholsterBenchStyle: null,
      widthInches: null,
      heightInches: null,
      depthInches: null,
      fabricDescription: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      inventoryItemId: null,
      baseballStitching: null,
      weltTop: null,
      weltBottom: null,
      trimTop: null,
      trimBottom: null,
      trimDescription: null,
      nailheadTop: null,
      nailheadBottom: null,
      nailheadColor: null,
      buttons: null,
      buttonCount: null,
      reupholsterSkirtType: null,
    },
  },
  reupholster_ottoman: {
    defaults: {
      ottomanShape: null,
      widthInches: null,
      heightInches: null,
      depthInches: null,
      fabricDescription: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      inventoryItemId: null,
      attachedPillowTop: null,
      tufted: null,
      baseballStitching: null,
      weltTop: null,
      weltBottom: null,
      trimTop: null,
      trimBottom: null,
      trimDescription: null,
      nailheadTop: null,
      nailheadBottom: null,
      nailheadColor: null,
      buttons: null,
      buttonCount: null,
      reupholsterSkirtType: null,
    },
  },
  reupholster_pop_seat: {
    defaults: {
      widthInches: null,
      depthInches: null,
      fabricDescription: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      inventoryItemId: null,
      hrFoamDacron: null,
      weltBottom: null,
    },
  },
  reupholster_dining_chair: {
    defaults: {
      diningChairConfig: null,
      widthInches: null,
      depthInches: null,
      backWidthInches: null,
      backHeightInches: null,
      fabricDescription: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      inventoryItemId: null,
      seatFinish: null,
      seatWeltPlacement: null,
      bandingSize: null,
      seatNailheadColor: null,
      insideBackFinish: null,
      insideBackNailheadColor: null,
      outsideBackFinish: null,
      outsideBackNailheadColor: null,
      skirt: null,
      skirtDropInches: null,
      touchUpWood: null,
      refinishOrPaint: null,
    },
  },
  pin_board: {
    defaults: {
      widthInches: null,
      heightInches: null,
      fabricDescription: null,
      fabricWidthInches: null,
      patternRepeatInches: null,
      inventoryItemId: null,
      pinBoardFrame: null,
      pinBoardFramePaintColor: null,
      pinBoardRibbonNailheads: null,
      trimDescription: null,
      nailheadColor: null,
      pinBoardMounting: null,
      pinBoardMeasureTemplate: null,
    },
  },
};

const blankCustomItemForm = {
  section: "other" as string,
  description: "",
  quantity: "1",
  unitPrice: "",
  notes: "",
};

export default function DraftQuoteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState({
    billToAddressId: "" as number | "",
    shipToAddressId: "" as number | "",
    notes: "",
  });
  const [savingDetails, setSavingDetails] = useState(false);
  const billToPickerRef = useRef<AddressPickerHandle>(null);
  const shipToPickerRef = useRef<AddressPickerHandle>(null);

  const [showAddObjectForm, setShowAddObjectForm] = useState(false);
  const [newObjectData, setNewObjectData] = useState(blankObjectForm);
  const [addingItemToObjectId, setAddingItemToObjectId] = useState<
    number | null
  >(null);
  const [newCustomItemData, setNewCustomItemData] =
    useState(blankCustomItemForm);
  const [error, setError] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [resyncMessage, setResyncMessage] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState({
    description: "",
    quantity: "",
    unitPrice: "",
  });
  const [uploadingObjectId, setUploadingObjectId] = useState<number | null>(
    null,
  );
  const [mathObjectIds, setMathObjectIds] = useState<number[]>([]);
  const [typeFormInitialValues, setTypeFormInitialValues] = useState<Record<
    string,
    any
  > | null>(null);
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  // Set by "Go back" — when non-null, the Add Object form is in edit mode:
  // saving calls updateDraftQuoteObject (recompute in place) instead of
  // addDraftQuoteObject (create).
  const [editingObjectId, setEditingObjectId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetRef = useRef<number | null>(null);
  const typeFormRef = useRef<ObjectTypeFormHandle>(null);

  const { data, loading, refetch } = useQuery(DRAFT_QUOTE_BY_ID_QUERY, {
    variables: { id: parseInt(id!) },
    skip: !id,
  });

  // Drives the "finalizing will add these charges" banner at the bottom of
  // the page — reads finalizeQuotePlugin.ts's own computeFinalizationExtras
  // via GraphQL rather than re-deriving the Measure Charge/Remote rules
  // here, so the preview can never drift from what finalize actually does.
  const { data: finalizationPreviewData, refetch: refetchFinalizationPreview } =
    useQuery(DRAFT_QUOTE_FINALIZATION_PREVIEW_QUERY, {
      variables: { draftQuoteId: parseInt(id!) },
      skip: !id,
    });
  const finalizationPreview =
    finalizationPreviewData?.draftQuoteFinalizationPreview;

  const [addDraftQuoteObject, { loading: creatingObject }] = useMutation(
    ADD_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateDraftQuoteObject, { loading: updatingObject }] = useMutation(
    UPDATE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  // Shirred Valance is the one item type whose add/update goes through its
  // own dedicated mutations instead of the generic pair above — see
  // handleAddObject's branch below.
  const [addShirredValanceDraftQuoteObject] = useMutation(
    ADD_SHIRRED_VALANCE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateShirredValanceDraftQuoteObject] = useMutation(
    UPDATE_SHIRRED_VALANCE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  // Box Spring Cover, Duvet, Coverlet, and both Tablecloth types also use
  // their own dedicated mutations, same deviation as Shirred Valance — see
  // DEDICATED_TYPE_CONFIG/handleAddObject below.
  const [addBoxSpringCoverDraftQuoteObject] = useMutation(
    ADD_BOX_SPRING_COVER_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateBoxSpringCoverDraftQuoteObject] = useMutation(
    UPDATE_BOX_SPRING_COVER_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addAustrianShadeDraftQuoteObject] = useMutation(
    ADD_AUSTRIAN_SHADE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateAustrianShadeDraftQuoteObject] = useMutation(
    UPDATE_AUSTRIAN_SHADE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addBalloonShadeDraftQuoteObject] = useMutation(
    ADD_BALLOON_SHADE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateBalloonShadeDraftQuoteObject] = useMutation(
    UPDATE_BALLOON_SHADE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addDuvetDraftQuoteObject] = useMutation(
    ADD_DUVET_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateDuvetDraftQuoteObject] = useMutation(
    UPDATE_DUVET_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addCoverletDraftQuoteObject] = useMutation(
    ADD_COVERLET_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateCoverletDraftQuoteObject] = useMutation(
    UPDATE_COVERLET_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addTableclothRoundDraftQuoteObject] = useMutation(
    ADD_TABLECLOTH_ROUND_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateTableclothRoundDraftQuoteObject] = useMutation(
    UPDATE_TABLECLOTH_ROUND_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addTableclothRectangleDraftQuoteObject] = useMutation(
    ADD_TABLECLOTH_RECTANGLE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateTableclothRectangleDraftQuoteObject] = useMutation(
    UPDATE_TABLECLOTH_RECTANGLE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addBenchDraftQuoteObject] = useMutation(
    ADD_BENCH_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateBenchDraftQuoteObject] = useMutation(
    UPDATE_BENCH_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addCorniceDraftQuoteObject] = useMutation(
    ADD_CORNICE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateCorniceDraftQuoteObject] = useMutation(
    UPDATE_CORNICE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addBedDraftQuoteObject] = useMutation(
    ADD_BED_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateBedDraftQuoteObject] = useMutation(
    UPDATE_BED_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addReupholsterBenchDraftQuoteObject] = useMutation(
    ADD_REUPHOLSTER_BENCH_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateReupholsterBenchDraftQuoteObject] = useMutation(
    UPDATE_REUPHOLSTER_BENCH_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addReupholsterOttomanDraftQuoteObject] = useMutation(
    ADD_REUPHOLSTER_OTTOMAN_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateReupholsterOttomanDraftQuoteObject] = useMutation(
    UPDATE_REUPHOLSTER_OTTOMAN_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addReupholsterPopSeatDraftQuoteObject] = useMutation(
    ADD_REUPHOLSTER_POP_SEAT_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateReupholsterPopSeatDraftQuoteObject] = useMutation(
    UPDATE_REUPHOLSTER_POP_SEAT_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addReupholsterDiningChairDraftQuoteObject] = useMutation(
    ADD_REUPHOLSTER_DINING_CHAIR_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updateReupholsterDiningChairDraftQuoteObject] = useMutation(
    UPDATE_REUPHOLSTER_DINING_CHAIR_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [addPinBoardDraftQuoteObject] = useMutation(
    ADD_PIN_BOARD_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [updatePinBoardDraftQuoteObject] = useMutation(
    UPDATE_PIN_BOARD_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [archiveDraftQuoteObject] = useMutation(
    ARCHIVE_DRAFT_QUOTE_OBJECT_MUTATION,
  );
  const [archiveDraftQuoteObjectImage] = useMutation(
    ARCHIVE_DRAFT_QUOTE_OBJECT_IMAGE_MUTATION,
  );
  const [createDraftQuoteObjectItem, { loading: creatingItem }] = useMutation(
    CREATE_DRAFT_QUOTE_OBJECT_ITEM_MUTATION,
  );
  const [updateDraftQuoteObjectItem] = useMutation(
    UPDATE_DRAFT_QUOTE_OBJECT_ITEM_MUTATION,
  );
  const [updateDraftQuoteObjectItemProductType] = useMutation(
    UPDATE_DRAFT_QUOTE_OBJECT_ITEM_PRODUCT_TYPE_MUTATION,
  );
  const [finalizeQuote] = useMutation(FINALIZE_QUOTE_MUTATION);
  const [resyncFinalizedQuote] = useMutation(RESYNC_FINALIZED_QUOTE_MUTATION);
  const [updateDraftQuote] = useMutation(UPDATE_DRAFT_QUOTE_MUTATION);
  const [createAddress] = useMutation(CREATE_ADDRESS_MUTATION);

  const draftQuote: DraftQuoteData | undefined = data?.draftQuoteById;
  // PostGraphile serializes enum fields using their GraphQL enum name, which is
  // upper-cased (e.g. "ROLLER_SHADE"), not the original lowercase Postgres
  // value ("roller_shade") that WINDOW_TYPES/SOFT_GOODS_TYPES/ITEM_TYPE_FORM_COMPONENTS
  // are keyed by — normalize it back here, once, for every downstream comparison.
  const objects = (draftQuote?.draftQuoteObjectsByDraftQuoteId?.nodes || [])
    .filter((o) => !o.archivedAt)
    .map((o) => ({
      ...o,
      itemType: o.itemType
        ? (String(o.itemType).toLowerCase() as ItemTypeEnum)
        : null,
      mechanism: o.mechanism ? String(o.mechanism).toLowerCase() : null,
    }));

  const selectedType = newObjectData.itemType as ItemTypeEnum | "";
  const SelectedTypeForm = selectedType
    ? ITEM_TYPE_FORM_COMPONENTS[selectedType]
    : null;

  const pObj = (patch: Partial<typeof blankObjectForm>) =>
    setNewObjectData({ ...newObjectData, ...patch });
  const resetTypeChange = (itemType: ItemTypeEnum | "") => {
    setNewObjectData({
      ...blankObjectForm,
      name: newObjectData.name,
      quantity: newObjectData.quantity,
      notes: newObjectData.notes,
      itemType,
    });
    setTypeFormInitialValues(null);
    setEditingObjectId(null);
    setFormInstanceKey((k) => k + 1);
  };

  // Maps each DEDICATED_TYPE_CONFIG key to its mutation functions (the hooks
  // themselves are called unconditionally above; this just groups the
  // already-bound functions for lookup by item type in handleAddObject).
  const dedicatedMutations: Partial<
    Record<
      ItemTypeEnum,
      { add: (opts: any) => Promise<any>; update: (opts: any) => Promise<any> }
    >
  > = {
    box_spring_cover: {
      add: addBoxSpringCoverDraftQuoteObject,
      update: updateBoxSpringCoverDraftQuoteObject,
    },
    duvet: {
      add: addDuvetDraftQuoteObject,
      update: updateDuvetDraftQuoteObject,
    },
    coverlet: {
      add: addCoverletDraftQuoteObject,
      update: updateCoverletDraftQuoteObject,
    },
    tablecloth_round: {
      add: addTableclothRoundDraftQuoteObject,
      update: updateTableclothRoundDraftQuoteObject,
    },
    tablecloth_rectangle: {
      add: addTableclothRectangleDraftQuoteObject,
      update: updateTableclothRectangleDraftQuoteObject,
    },
    austrian_shade: {
      add: addAustrianShadeDraftQuoteObject,
      update: updateAustrianShadeDraftQuoteObject,
    },
    balloon_shade: {
      add: addBalloonShadeDraftQuoteObject,
      update: updateBalloonShadeDraftQuoteObject,
    },
    bench: {
      add: addBenchDraftQuoteObject,
      update: updateBenchDraftQuoteObject,
    },
    cornice: {
      add: addCorniceDraftQuoteObject,
      update: updateCorniceDraftQuoteObject,
    },
    bed: {
      add: addBedDraftQuoteObject,
      update: updateBedDraftQuoteObject,
    },
    reupholster_bench: {
      add: addReupholsterBenchDraftQuoteObject,
      update: updateReupholsterBenchDraftQuoteObject,
    },
    reupholster_ottoman: {
      add: addReupholsterOttomanDraftQuoteObject,
      update: updateReupholsterOttomanDraftQuoteObject,
    },
    reupholster_pop_seat: {
      add: addReupholsterPopSeatDraftQuoteObject,
      update: updateReupholsterPopSeatDraftQuoteObject,
    },
    reupholster_dining_chair: {
      add: addReupholsterDiningChairDraftQuoteObject,
      update: updateReupholsterDiningChairDraftQuoteObject,
    },
    pin_board: {
      add: addPinBoardDraftQuoteObject,
      update: updatePinBoardDraftQuoteObject,
    },
  };

  const handleAddObject = async () => {
    if (!draftQuote?.id) return;
    const typeFields = typeFormRef.current?.getValues();
    if (typeFields === null) return; // component found a validation problem and reported its own error

    setError(null);

    // Shirred Valance uses its own dedicated mutations with a narrow args
    // list (see draftQuotes.ts) instead of the generic addDraftQuoteObject/
    // updateDraftQuoteObject calls below — a deliberate deviation from every
    // other item type.
    if (newObjectData.itemType === "shirred_valance") {
      const shirredVariables = {
        draftQuoteId: draftQuote.id,
        name: newObjectData.name.trim() || null,
        quantity: parseInt(newObjectData.quantity, 10) || 1,
        notes: newObjectData.notes.trim() || null,
        widthInches: null,
        mountType: null,
        spaceAboveWindowInches: null,
        fabricDescription: null,
        liningType: null,
        interliningType: null,
        trimPlacement: null,
        fabricWidthInches: null,
        fabricRepeatInches: null,
        inventoryItemId: null,
        liningDescription: null,
        ...typeFields,
      };
      try {
        if (editingObjectId != null) {
          await updateShirredValanceDraftQuoteObject({
            variables: { objectId: editingObjectId, ...shirredVariables },
          });
        } else {
          await addShirredValanceDraftQuoteObject({
            variables: { sortOrder: objects.length, ...shirredVariables },
          });
        }
        await Promise.all([refetch(), refetchFinalizationPreview()]);
        setNewObjectData(blankObjectForm);
        setTypeFormInitialValues(null);
        setEditingObjectId(null);
        setShowAddObjectForm(false);
      } catch (err: any) {
        setError(
          err.message ||
            (editingObjectId != null
              ? "Failed to update object"
              : "Failed to add object"),
        );
      }
      return;
    }

    // Box Spring Cover, Duvet, Coverlet, and both Tablecloth types also use
    // their own dedicated add/update mutations (narrow args list, same
    // deviation Shirred Valance established above) instead of the generic
    // pair below.
    const dedicatedConfig =
      DEDICATED_TYPE_CONFIG[newObjectData.itemType as ItemTypeEnum];
    if (dedicatedConfig) {
      const dedicatedVariables = {
        draftQuoteId: draftQuote.id,
        name: newObjectData.name.trim() || null,
        quantity: parseInt(newObjectData.quantity, 10) || 1,
        notes: newObjectData.notes.trim() || null,
        ...dedicatedConfig.defaults,
        ...typeFields,
      };
      const mutations =
        dedicatedMutations[newObjectData.itemType as ItemTypeEnum]!;
      try {
        if (editingObjectId != null) {
          await mutations.update({
            variables: { objectId: editingObjectId, ...dedicatedVariables },
          });
        } else {
          await mutations.add({
            variables: { sortOrder: objects.length, ...dedicatedVariables },
          });
        }
        await Promise.all([refetch(), refetchFinalizationPreview()]);
        setNewObjectData(blankObjectForm);
        setTypeFormInitialValues(null);
        setEditingObjectId(null);
        setShowAddObjectForm(false);
      } catch (err: any) {
        setError(
          err.message ||
            (editingObjectId != null
              ? "Failed to update object"
              : "Failed to add object"),
        );
      }
      return;
    }

    const commonVariables = {
      draftQuoteId: draftQuote.id,
      name: newObjectData.name.trim() || null,
      quantity: parseInt(newObjectData.quantity, 10) || 1,
      notes: newObjectData.notes.trim() || null,
      itemType: newObjectData.itemType || null,
      mountType: null,
      widthInches: null,
      heightInches: null,
      depthInches: null,
      fabricDescription: null,
      liningType: null,
      bumpInterlining: false,
      operatingSystem: null,
      hardwareType: null,
      rollDirection: null,
      driveSide: null,
      chainColor: null,
      bedSize: null,
      weltType: null,
      insertType: null,
      dustskirtStyle: null,
      spaceAboveWindowInches: null,
      trimPlacement: null,
      fabricWidthInches: null,
      liningWidthInches: null,
      flapSizeInches: null,
      patternRepeatInches: null,
      liningDescription: null,
      fasciaSize: null,
      mechanism: null,
      inventoryItemId: null,
      interliningType: null,
      fabricRepeatInches: null,
      rodFaceInches: null,
      overlapInches: null,
      returnInches: null,
      hemSize: null,
      pleatType: null,
      ruffle: false,
      shamSize: null,
      dropInches: null,
      seatHeightInches: null,
      seatDepthInches: null,
      armHeightInches: null,
      backHeightInches: null,
      legHeightInches: null,
      cushionThicknessInches: null,
      armThicknessInches: null,
      seatDeckThicknessInches: null,
      backCushionThicknessInches: null,
      legProfile: null,
      furnitureType: null,
      customWoodFascia: false,
      customWoodFasciaPaintColor: null,
      ...typeFields,
    };
    try {
      if (editingObjectId != null) {
        await updateDraftQuoteObject({
          variables: { objectId: editingObjectId, ...commonVariables },
        });
      } else {
        await addDraftQuoteObject({
          variables: { sortOrder: objects.length, ...commonVariables },
        });
      }
      await Promise.all([refetch(), refetchFinalizationPreview()]);
      setNewObjectData(blankObjectForm);
      setTypeFormInitialValues(null);
      setEditingObjectId(null);
      setShowAddObjectForm(false);
    } catch (err: any) {
      setError(
        err.message ||
          (editingObjectId != null
            ? "Failed to update object"
            : "Failed to add object"),
      );
    }
  };

  const handleAddCustomItem = async (objectId: number) => {
    if (!newCustomItemData.description.trim()) {
      setError("Description is required");
      return;
    }
    setError(null);
    const unitPriceCents = newCustomItemData.unitPrice
      ? Math.round(parseFloat(newCustomItemData.unitPrice) * 100)
      : 0;
    try {
      await createDraftQuoteObjectItem({
        variables: {
          draftQuoteId: draftQuote!.id,
          draftQuoteObjectId: objectId,
          section: newCustomItemData.section,
          description: newCustomItemData.description.trim(),
          quantity: parseFloat(newCustomItemData.quantity) || 1,
          unitPrice: unitPriceCents,
          notes: newCustomItemData.notes.trim() || null,
        },
      });
      await Promise.all([refetch(), refetchFinalizationPreview()]);
      setNewCustomItemData(blankCustomItemForm);
      setAddingItemToObjectId(null);
    } catch (err: any) {
      setError(err.message || "Failed to add item");
    }
  };

  const handleFinalize = async () => {
    if (!draftQuote?.id) return;
    if (objects.length === 0) {
      setError("Add at least one object before finalizing");
      return;
    }
    setError(null);
    setFinalizing(true);
    try {
      const result = await finalizeQuote({
        variables: { draftQuoteId: draftQuote.id },
      });
      const quoteId = result.data?.finalizeQuote?.quoteId;
      if (quoteId) navigate(`/quotes/${quoteId}`);
    } catch (err: any) {
      setError(err.message || "Failed to finalize quote");
      setFinalizing(false);
    }
  };

  // Rewrites the already-finalized quote's line items from this draft's
  // current objects — for reconciling hand edits made here (e.g. to match a
  // change QuickBooks already has) onto the finalized quote, without
  // creating a new quote id or touching QuickBooks. Stays on this page
  // (unlike Finalize, which navigates away) so further edits can follow.
  const handleResyncFinalizedQuote = async () => {
    if (!draftQuote?.id) return;
    setError(null);
    setResyncMessage(null);
    setResyncing(true);
    try {
      const result = await resyncFinalizedQuote({
        variables: { draftQuoteId: draftQuote.id },
      });
      const count = result.data?.resyncFinalizedQuote?.lineItemCount;
      setResyncMessage(
        `Finalized quote updated — ${count} line item${count === 1 ? "" : "s"}.`,
      );
    } catch (err: any) {
      setError(err.message || "Failed to update the finalized quote");
    } finally {
      setResyncing(false);
    }
  };

  const startEditDraftItem = (item: DraftItem) => {
    setEditingItemId(item.id);
    setEditValues({
      description: item.description,
      quantity: String(Number(item.quantity)),
      unitPrice: String(Number(item.unitPrice) / 100),
    });
  };

  const handleSaveDraftItem = async (itemId: number) => {
    setError(null);
    try {
      await updateDraftQuoteObjectItem({
        variables: {
          id: itemId,
          description: editValues.description,
          quantity: parseFloat(editValues.quantity),
          unitPrice: Math.round(parseFloat(editValues.unitPrice) * 100),
        },
      });
      await Promise.all([refetch(), refetchFinalizationPreview()]);
      setEditingItemId(null);
    } catch (err: any) {
      setError(err.message || "Failed to update item");
    }
  };

  const handleProductTypeChange = async (
    itemId: number,
    productType: string,
  ) => {
    setError(null);
    try {
      await updateDraftQuoteObjectItemProductType({
        variables: { id: itemId, productType: productType || null },
      });
      await Promise.all([refetch(), refetchFinalizationPreview()]);
    } catch (err: any) {
      setError(err.message || "Failed to update product type");
    }
  };

  const handleArchiveObject = async (objectId: number) => {
    await archiveDraftQuoteObject({
      variables: { id: objectId, archivedAt: new Date().toISOString() },
    });
    await Promise.all([refetch(), refetchFinalizationPreview()]);
  };

  // "Go back": re-open a saved object's inputs for editing, in place — the
  // object list render swaps this object's card for the form at its same
  // position (editingObjectId === obj.id), so unlike the old archive-based
  // approach, nothing needs to be hidden/restored server-side just to start
  // editing; it's a pure client-state change, so no refetch is needed here.
  // Saving calls updateDraftQuoteObject, which recomputes the object in place
  // without touching sort_order, so it stays in the same list position.
  const handleGoBack = (obj: DraftObject) => {
    setError(null);
    setNewObjectData({
      name: obj.name || "",
      quantity: String(obj.quantity),
      itemType: obj.itemType || "",
      notes: obj.notes || "",
    });
    setTypeFormInitialValues(buildInitialValuesFromObject(obj));
    setEditingObjectId(obj.id);
    setFormInstanceKey((k) => k + 1);
    setShowAddObjectForm(true);
  };

  // Cancel an in-place edit (or close the add form) without saving —
  // resets to a blank, closed state. No server call needed since nothing
  // is written until Save.
  const handleCancelObjectForm = () => {
    setNewObjectData(blankObjectForm);
    setTypeFormInitialValues(null);
    setEditingObjectId(null);
    setShowAddObjectForm(false);
    setFormInstanceKey((k) => k + 1);
    setError(null);
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const targetId = uploadTargetRef.current;
    e.target.value = "";
    if (files.length === 0 || !targetId) return;

    setError(null);
    setUploadingObjectId(targetId);
    const failed: string[] = [];
    try {
      const token = localStorage.getItem("authToken");
      for (const file of files) {
        const formData = new FormData();
        formData.append("photo", file);
        formData.append("objectId", String(targetId));
        const resp = await fetch(`${SERVER_URL}/upload/object-image`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          failed.push(`${file.name}: ${body.error || "Upload failed"}`);
        }
      }
      if (failed.length > 0) {
        setError(
          `Failed to upload ${failed.length} of ${files.length} photo(s) — ${failed.join("; ")}`,
        );
      }
      await Promise.all([refetch(), refetchFinalizationPreview()]);
    } catch (err: any) {
      setError(err.message || "Failed to upload photos");
    } finally {
      setUploadingObjectId(null);
      uploadTargetRef.current = null;
    }
  };

  const handleArchiveImage = async (imageId: number) => {
    await archiveDraftQuoteObjectImage({
      variables: { id: imageId, archivedAt: new Date().toISOString() },
    });
    await Promise.all([refetch(), refetchFinalizationPreview()]);
  };

  if (loading) return <div className="quote-detail-page">Loading...</div>;

  // Shared hidden file input — triggered programmatically per object card
  const photoFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/jpeg,image/png,image/gif,image/webp"
      multiple
      style={{ display: "none" }}
      onChange={handleUploadImage}
    />
  );

  if (!draftQuote) {
    return (
      <div className="quote-detail-page">
        <button
          onClick={() => navigate("/quotes")}
          className="back-button btn-icon"
        >
          <ArrowBackIcon fontSize="small" /> Back to Quotes
        </button>
        <div className="error-message">Draft quote not found</div>
      </div>
    );
  }

  const addressText = (addr: AddressOption | null) =>
    addr ? `${addr.street}, ${addr.city}, ${addr.state} ${addr.zip}` : "—";

  const billToAddresses =
    draftQuote.jobByJobId.customerByCustomerId.addressesByCustomerId.nodes;
  const shipToAddresses = dedupeAddresses([
    ...(draftQuote.jobByJobId.addressByAddressId
      ? [draftQuote.jobByJobId.addressByAddressId]
      : []),
    ...billToAddresses,
  ]);

  const openEditDetails = () => {
    setDetailsForm({
      billToAddressId: draftQuote.addressByBillToAddressId?.id ?? "",
      shipToAddressId: draftQuote.addressByShipToAddressId?.id ?? "",
      notes: draftQuote.notes || "",
    });
    setEditingDetails(true);
  };

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    setError(null);
    try {
      const billToAddressId =
        (await billToPickerRef.current?.commit()) ??
        detailsForm.billToAddressId;
      const shipToAddressId =
        (await shipToPickerRef.current?.commit()) ??
        detailsForm.shipToAddressId;
      await updateDraftQuote({
        variables: {
          id: draftQuote.id,
          billToAddressId: billToAddressId || null,
          shipToAddressId: shipToAddressId || null,
          notes: detailsForm.notes || null,
        },
      });
      setEditingDetails(false);
      await Promise.all([refetch(), refetchFinalizationPreview()]);
    } catch (err: any) {
      setError(err.message || "Failed to save quote details");
    } finally {
      setSavingDetails(false);
    }
  };

  const createAddressForCustomer = async (fields: {
    street: string;
    city: string;
    state: string;
    zip: string;
  }) => {
    const result = await createAddress({
      variables: {
        customerId: draftQuote.jobByJobId.customerByCustomerId.id,
        domainId: user!.domain_id,
        street: fields.street,
        city: fields.city,
        state: fields.state,
        zip: fields.zip,
        addressType: "primary",
      },
    });
    return result.data.createAddress.address;
  };

  const alreadyFinalized = !!draftQuote.finalizedQuoteId;

  // The shared add/edit object form — rendered either after the object list
  // (adding a new object) or in place of a specific card within the list
  // (editingObjectId === obj.id, "Go back"). Pulled into a function so both
  // call sites share the exact same JSX/state instead of duplicating it.
  const renderObjectForm = () => (
    <div className="add-line-form">
      {/* Row 1: label / type / qty */}
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 2 }}>
          <label>
            Location / Label{" "}
            <span style={{ color: "#9ca3af", fontWeight: 400 }}>
              (optional)
            </span>
          </label>
          <input
            type="text"
            className="input"
            placeholder="e.g., Left Window, Bay Window"
            value={newObjectData.name}
            onChange={(e) => pObj({ name: e.target.value })}
            disabled={creatingObject}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Product Type</label>
          <select
            className="input"
            value={newObjectData.itemType}
            onChange={(e) =>
              resetTypeChange(e.target.value as ItemTypeEnum | "")
            }
            disabled={creatingObject}
          >
            <option value="">Select type...</option>
            <optgroup label="── Window Treatments ──">
              {WINDOW_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="── Soft Goods ──">
              {SOFT_GOODS_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="── Furniture ──">
              {FURNITURE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="── Benches ──">
              {BENCH_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="── Cornices ──">
              {CORNICE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="── Beds ──">
              {BED_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="── Reupholster ──">
              {[...REUPHOLSTER_BENCH_TYPES, ...REUPHOLSTER_OTTOMAN_TYPES, ...REUPHOLSTER_POP_SEAT_TYPES, ...REUPHOLSTER_DINING_CHAIR_TYPES].map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="── Pin Boards ──">
              {PIN_BOARD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <div className="form-group" style={{ width: 80 }}>
          <label>Qty</label>
          <input
            type="number"
            min="1"
            step="1"
            className="input"
            value={newObjectData.quantity}
            onChange={(e) => pObj({ quantity: e.target.value })}
            disabled={creatingObject}
          />
        </div>
      </div>

      {/* Type-specific fields — each product type owns its own question set */}
      {SelectedTypeForm && (
        <SelectedTypeForm
          key={formInstanceKey}
          ref={typeFormRef}
          disabled={creatingObject || updatingObject}
          initialValues={typeFormInitialValues}
        />
      )}

      {/* Shared across every item type — shop notes that never affect price
          (e.g. "with tailored front pleats and top to remain pleated even
          when down"). Shows on the finalized quote and auto-fills the Work
          Order description when this object is dragged in. */}
      <div className="form-group">
        <label>
          Notes{" "}
          <span style={{ color: "#9ca3af", fontWeight: 400 }}>
            (optional, shown on quote &amp; work order — doesn't affect price)
          </span>
        </label>
        <textarea
          className="input"
          rows={2}
          placeholder="e.g., with tailored pleats"
          value={newObjectData.notes}
          onChange={(e) => pObj({ notes: e.target.value })}
          disabled={creatingObject || updatingObject}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={handleAddObject}
          className="save-button"
          disabled={creatingObject || updatingObject}
        >
          {editingObjectId != null
            ? updatingObject
              ? "Saving..."
              : "Save Changes"
            : creatingObject
              ? "Adding..."
              : "Add Object"}
        </button>
        {editingObjectId != null && (
          <button
            onClick={handleCancelObjectForm}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#6b7280",
              fontSize: 13,
              padding: "4px 8px",
            }}
            disabled={updatingObject}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="quote-detail-page">
      {photoFileInput}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <button
          onClick={() => navigate("/quotes")}
          className="back-button btn-icon"
          style={{ marginBottom: 0 }}
        >
          <ArrowBackIcon fontSize="small" /> Back to Quotes
        </button>
        {alreadyFinalized ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              className="btn-icon"
              style={{
                background: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                padding: "8px 14px",
                fontSize: 14,
                fontWeight: 600,
                cursor: resyncing ? "default" : "pointer",
              }}
              onClick={handleResyncFinalizedQuote}
              disabled={resyncing}
              title="Rewrite the finalized quote's line items to match this draft's current objects"
            >
              {resyncing ? "Updating..." : "Update Finalized Quote"}
            </button>
            <button
              className="save-button btn-icon"
              onClick={() => navigate(`/quotes/${draftQuote.finalizedQuoteId}`)}
            >
              View Finalized Quote <ArrowForwardIcon fontSize="small" />
            </button>
          </div>
        ) : (
          <button
            className="save-button"
            onClick={handleFinalize}
            disabled={finalizing}
            style={{ backgroundColor: "#10b981" }}
          >
            {finalizing ? (
              "Finalizing..."
            ) : (
              <span className="btn-icon">
                Save + Finalize <ArrowForwardIcon fontSize="small" />
              </span>
            )}
          </button>
        )}
      </div>

      <div className="detail-container">
        <div className="detail-header">
          <h1>Draft: {draftQuote.name}</h1>
          <span
            className="status-badge"
            style={{ backgroundColor: "#fef3c7", color: "#92400e" }}
          >
            DRAFT
          </span>
        </div>

        {error && <div className="error-message">{error}</div>}

        {resyncMessage && (
          <div
            style={{
              padding: "12px 16px",
              backgroundColor: "#dbeafe",
              color: "#1e40af",
              borderRadius: 6,
              marginBottom: 16,
              fontSize: 14,
            }}
          >
            {resyncMessage}
          </div>
        )}

        {alreadyFinalized && (
          <div
            style={{
              padding: "12px 16px",
              backgroundColor: "#d1fae5",
              color: "#065f46",
              borderRadius: 6,
              marginBottom: 16,
              fontSize: 14,
            }}
          >
            This draft has been finalized.{" "}
            <button
              style={{
                background: "none",
                border: "none",
                color: "#065f46",
                fontWeight: 700,
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => navigate(`/quotes/${draftQuote.finalizedQuoteId}`)}
            >
              View the generated quote <ArrowForwardIcon fontSize="small" />
            </button>
          </div>
        )}

        <div className="quote-dashboard-grid">
          <div className="quote-dashboard-left">
            {/* Quote Details */}
            <div className="info-block">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3>Quote Details</h3>
                {!editingDetails && (
                  <button
                    className="btn-icon"
                    style={{
                      padding: "6px 12px",
                      background: "#f3f4f6",
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      fontSize: 13,
                      cursor: "pointer",
                      color: "#374151",
                    }}
                    onClick={openEditDetails}
                  >
                    <EditIcon fontSize="small" /> Edit
                  </button>
                )}
              </div>
              <div className="info-grid">
                <div className="info-row">
                  <label>Customer</label>
                  <div>
                    <a
                      href={`/customers/${draftQuote.jobByJobId.customerByCustomerId.id}`}
                      style={{ color: "#3b82f6", textDecoration: "none" }}
                    >
                      {draftQuote.jobByJobId.customerByCustomerId.name}
                    </a>
                  </div>
                </div>
                <div className="info-row">
                  <label>Job</label>
                  <div>
                    <a
                      href={`/jobs/${draftQuote.jobByJobId.id}`}
                      style={{ color: "#3b82f6", textDecoration: "none" }}
                    >
                      {draftQuote.jobByJobId.name}
                    </a>
                  </div>
                </div>
                {editingDetails ? (
                  <>
                    <div className="info-row">
                      <label>Bill To</label>
                      <div style={{ maxWidth: 400 }}>
                        <AddressPicker
                          ref={billToPickerRef}
                          label=""
                          addresses={billToAddresses}
                          value={detailsForm.billToAddressId}
                          onChange={(v) =>
                            setDetailsForm({
                              ...detailsForm,
                              billToAddressId: v,
                            })
                          }
                          disabled={savingDetails}
                          onCreateAddress={createAddressForCustomer}
                        />
                      </div>
                    </div>
                    <div className="info-row">
                      <label>Ship To</label>
                      <div style={{ maxWidth: 400 }}>
                        <AddressPicker
                          ref={shipToPickerRef}
                          label=""
                          addresses={shipToAddresses}
                          value={detailsForm.shipToAddressId}
                          onChange={(v) =>
                            setDetailsForm({
                              ...detailsForm,
                              shipToAddressId: v,
                            })
                          }
                          disabled={savingDetails}
                          onCreateAddress={createAddressForCustomer}
                        />
                      </div>
                    </div>
                    <div className="info-row">
                      <label>Notes</label>
                      <textarea
                        style={{
                          width: "100%",
                          minHeight: 72,
                          padding: "9px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontSize: 14,
                          fontFamily: "inherit",
                          resize: "vertical",
                          boxSizing: "border-box",
                        }}
                        value={detailsForm.notes}
                        disabled={savingDetails}
                        onChange={(e) =>
                          setDetailsForm({
                            ...detailsForm,
                            notes: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="info-row">
                      <label></label>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="save-button"
                          disabled={savingDetails}
                          onClick={handleSaveDetails}
                        >
                          {savingDetails ? "Saving..." : "Save"}
                        </button>
                        <button
                          style={{
                            padding: "10px 16px",
                            background: "#f3f4f6",
                            color: "#374151",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                          disabled={savingDetails}
                          onClick={() => setEditingDetails(false)}
                        >
                          <CloseIcon fontSize="small" /> Cancel
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="info-row">
                      <label>Bill To</label>
                      <div>
                        {addressText(draftQuote.addressByBillToAddressId)}
                      </div>
                    </div>
                    <div className="info-row">
                      <label>Ship To</label>
                      <div>
                        {addressText(draftQuote.addressByShipToAddressId)}
                      </div>
                    </div>
                    {draftQuote.notes && (
                      <div className="info-row">
                        <label>Notes</label>
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {draftQuote.notes}
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div className="info-row">
                  <label>Created</label>
                  <div>
                    {new Date(draftQuote.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="quote-dashboard-right">
            {/* Objects Section */}
            <div className="section">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <h2>Objects ({objects.length})</h2>
                {/* Only shown here for an empty quote — once there's at least one
                object, the "+ Add Object" trigger moves below the list (see
                after objects.map() below) so it sits next to where the new
                card will actually appear. */}
                {objects.length === 0 && (
                  <button
                    className="add-button btn-icon"
                    onClick={() => {
                      setShowAddObjectForm(!showAddObjectForm);
                      setNewObjectData(blankObjectForm);
                      setTypeFormInitialValues(null);
                      setEditingObjectId(null);
                      setFormInstanceKey((k) => k + 1);
                      setError(null);
                    }}
                  >
                    {showAddObjectForm ? (
                      <CloseIcon fontSize="small" />
                    ) : (
                      <AddIcon fontSize="small" />
                    )}
                    {showAddObjectForm ? "Close" : "Add Object"}
                  </button>
                )}
              </div>

              {objects.length === 0 && showAddObjectForm && renderObjectForm()}

              {objects.length === 0 ? (
                <div className="empty-state">
                  No objects yet — click "Add Object" to get started.
                </div>
              ) : (
                objects.map((obj, idx) => {
                  // "Go back" swaps this card for the shared form in place, at
                  // this same list position, instead of jumping to a form
                  // elsewhere on the page — see handleGoBack/renderObjectForm.
                  if (editingObjectId === obj.id) {
                    return <div key={obj.id}>{renderObjectForm()}</div>;
                  }

                  const items =
                    obj.draftQuoteObjectItemsByDraftQuoteObjectId.nodes.filter(
                      (i) => !i.archivedAt,
                    );
                  const summary = objectSummaryLine(obj);
                  const isAddingItem = addingItemToObjectId === obj.id;
                  const showMath = mathObjectIds.includes(obj.id);

                  return (
                    <div key={obj.id} className="object-card">
                      <div className="object-card-header">
                        <div>
                          <span className="object-card-title">
                            {obj.name || `Object ${idx + 1}`}
                          </span>
                          {summary && (
                            <span
                              style={{
                                fontSize: 13,
                                color: "#6b7280",
                                marginLeft: 10,
                              }}
                            >
                              {summary}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {items.some((it) => it.formulaNote) && (
                            <button
                              onClick={() =>
                                setMathObjectIds(
                                  showMath
                                    ? mathObjectIds.filter(
                                        (oid) => oid !== obj.id,
                                      )
                                    : [...mathObjectIds, obj.id],
                                )
                              }
                              style={{
                                background: "none",
                                border: "1px solid #d1d5db",
                                borderRadius: 4,
                                cursor: "pointer",
                                color: showMath ? "#2563eb" : "#6b7280",
                                fontSize: 12,
                                padding: "2px 8px",
                              }}
                              title="Show how each line item was calculated"
                            >
                              {showMath ? "Hide math" : "Show math"}
                            </button>
                          )}
                          <button
                            className="add-item-button btn-icon"
                            onClick={() => {
                              setAddingItemToObjectId(
                                isAddingItem ? null : obj.id,
                              );
                              setNewCustomItemData(blankCustomItemForm);
                              setError(null);
                            }}
                            disabled={creatingItem}
                          >
                            {isAddingItem ? (
                              <CloseIcon fontSize="small" />
                            ) : (
                              <AddIcon fontSize="small" />
                            )}
                            {isAddingItem ? "Close" : "Add Custom Item"}
                          </button>
                          <button
                            onClick={() => handleGoBack(obj)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#9ca3af",
                              fontSize: 12,
                              padding: "2px 4px",
                            }}
                            title="Re-edit this object's inputs"
                          >
                            <UndoIcon sx={{ fontSize: 14 }} />
                            Go back
                          </button>
                          <button
                            onClick={() => handleArchiveObject(obj.id)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#9ca3af",
                              fontSize: 12,
                              padding: "2px 4px",
                            }}
                            title="Archive this object"
                          >
                            <ArchiveIcon sx={{ fontSize: 14 }} />
                            Archive
                          </button>
                        </div>
                      </div>

                      {obj.notes && (
                        <div
                          style={{
                            padding: "0 16px 10px",
                            fontSize: 13,
                            color: "#4b5563",
                            fontStyle: "italic",
                          }}
                        >
                          Note: {obj.notes}
                        </div>
                      )}

                      {/* Calculated items by section */}
                      {items.length > 0 && (
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: 13,
                          }}
                        >
                          <thead>
                            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                              <th
                                style={{
                                  textAlign: "left",
                                  padding: "6px 16px",
                                  color: "#6b7280",
                                  fontWeight: 600,
                                  width: 100,
                                }}
                              >
                                Section
                              </th>
                              <th
                                style={{
                                  textAlign: "left",
                                  padding: "6px 8px",
                                  color: "#6b7280",
                                  fontWeight: 600,
                                  width: 130,
                                }}
                              >
                                Product Type
                              </th>
                              <th
                                style={{
                                  textAlign: "left",
                                  padding: "6px 8px",
                                  color: "#6b7280",
                                  fontWeight: 600,
                                }}
                              >
                                Description
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: "6px 8px",
                                  color: "#6b7280",
                                  fontWeight: 600,
                                  width: 100,
                                }}
                              >
                                Qty
                              </th>
                              <th
                                style={{
                                  textAlign: "right",
                                  padding: "6px 16px",
                                  color: "#6b7280",
                                  fontWeight: 600,
                                  width: 100,
                                }}
                              >
                                Unit Price
                              </th>
                              <th style={{ width: 48 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {SECTION_ORDER.flatMap((section) =>
                              items
                                .filter((item) => item.section === section)
                                .map((item) => {
                                  const colors =
                                    SECTION_COLORS[item.section] ||
                                    SECTION_COLORS.other;
                                  const isEditing = editingItemId === item.id;
                                  return isEditing ? (
                                    <tr
                                      key={item.id}
                                      style={{
                                        borderBottom: "1px solid #f9fafb",
                                        backgroundColor: "#fffbeb",
                                      }}
                                    >
                                      <td style={{ padding: "6px 16px" }}>
                                        <span
                                          className="section-badge"
                                          style={{
                                            backgroundColor: colors.bg,
                                            color: colors.text,
                                            fontSize: 11,
                                          }}
                                        >
                                          {(
                                            SECTION_LABELS[
                                              item.section as keyof typeof SECTION_LABELS
                                            ] ?? item.section
                                          ).toUpperCase()}
                                        </span>
                                      </td>
                                      <td style={{ padding: "6px 8px" }}>
                                        <select
                                          className="input"
                                          style={{
                                            padding: "4px 6px",
                                            fontSize: 12,
                                            width: "100%",
                                          }}
                                          value={item.productType || ""}
                                          onChange={(e) =>
                                            handleProductTypeChange(
                                              item.id,
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">—</option>
                                          {PRODUCT_TYPE_OPTIONS.map((opt) => (
                                            <option
                                              key={opt.value}
                                              value={opt.value}
                                            >
                                              {opt.label}
                                            </option>
                                          ))}
                                        </select>
                                      </td>
                                      <td style={{ padding: "6px 8px" }}>
                                        <input
                                          className="input"
                                          style={{
                                            padding: "3px 8px",
                                            fontSize: 13,
                                            width: "100%",
                                          }}
                                          value={editValues.description}
                                          onChange={(e) =>
                                            setEditValues({
                                              ...editValues,
                                              description: e.target.value,
                                            })
                                          }
                                        />
                                      </td>
                                      <td style={{ padding: "6px 8px" }}>
                                        <input
                                          className="input"
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          style={{
                                            padding: "3px 8px",
                                            fontSize: 13,
                                            width: 80,
                                            textAlign: "right",
                                          }}
                                          value={editValues.quantity}
                                          onChange={(e) =>
                                            setEditValues({
                                              ...editValues,
                                              quantity: e.target.value,
                                            })
                                          }
                                        />
                                      </td>
                                      <td style={{ padding: "6px 16px" }}>
                                        <input
                                          className="input"
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          style={{
                                            padding: "3px 8px",
                                            fontSize: 13,
                                            width: 80,
                                            textAlign: "right",
                                          }}
                                          value={editValues.unitPrice}
                                          onChange={(e) =>
                                            setEditValues({
                                              ...editValues,
                                              unitPrice: e.target.value,
                                            })
                                          }
                                        />
                                      </td>
                                      <td
                                        style={{
                                          padding: "6px 8px",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        <button
                                          onClick={() =>
                                            handleSaveDraftItem(item.id)
                                          }
                                          style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            color: "#10b981",
                                            marginRight: 2,
                                          }}
                                          title="Save"
                                        >
                                          <CheckIcon fontSize="small" />
                                        </button>
                                        <button
                                          onClick={() => setEditingItemId(null)}
                                          style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            color: "#ef4444",
                                          }}
                                          title="Cancel"
                                        >
                                          <CloseIcon fontSize="small" />
                                        </button>
                                      </td>
                                    </tr>
                                  ) : (
                                    <tr
                                      key={item.id}
                                      style={{
                                        borderBottom: "1px solid #f9fafb",
                                      }}
                                    >
                                      <td style={{ padding: "7px 16px" }}>
                                        <span
                                          className="section-badge"
                                          style={{
                                            backgroundColor: colors.bg,
                                            color: colors.text,
                                            fontSize: 11,
                                          }}
                                        >
                                          {(
                                            SECTION_LABELS[
                                              item.section as keyof typeof SECTION_LABELS
                                            ] ?? item.section
                                          ).toUpperCase()}
                                        </span>
                                      </td>
                                      <td style={{ padding: "7px 8px" }}>
                                        <select
                                          className="input"
                                          style={{
                                            padding: "4px 6px",
                                            fontSize: 12,
                                            width: "100%",
                                          }}
                                          value={item.productType || ""}
                                          onChange={(e) =>
                                            handleProductTypeChange(
                                              item.id,
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">—</option>
                                          {PRODUCT_TYPE_OPTIONS.map((opt) => (
                                            <option
                                              key={opt.value}
                                              value={opt.value}
                                            >
                                              {opt.label}
                                            </option>
                                          ))}
                                        </select>
                                      </td>
                                      <td
                                        style={{
                                          padding: "7px 8px",
                                          color: "#111827",
                                        }}
                                      >
                                        {item.description}
                                        {item.formulaNote && !showMath && (
                                          <span className="formula-tooltip">
                                            <span className="formula-tooltip-icon">
                                              ⓘ
                                            </span>
                                            <span className="formula-tooltip-box">
                                              {item.formulaNote}
                                            </span>
                                          </span>
                                        )}
                                        {item.formulaNote && showMath && (
                                          <div
                                            style={{
                                              whiteSpace: "pre-line",
                                              fontFamily:
                                                "ui-monospace, monospace",
                                              fontSize: 11.5,
                                              color: "#6b7280",
                                              backgroundColor: "#f9fafb",
                                              border: "1px solid #f3f4f6",
                                              borderRadius: 4,
                                              padding: "5px 8px",
                                              marginTop: 4,
                                            }}
                                          >
                                            {item.formulaNote}
                                          </div>
                                        )}
                                      </td>
                                      <td
                                        style={{
                                          padding: "7px 8px",
                                          textAlign: "right",
                                          color: "#374151",
                                        }}
                                      >
                                        {formatQty(item.quantity, item.section)}
                                      </td>
                                      <td
                                        style={{
                                          padding: "7px 16px",
                                          textAlign: "right",
                                          color:
                                            Number(item.unitPrice) === 0
                                              ? "#9ca3af"
                                              : "#111827",
                                        }}
                                      >
                                        {formatPrice(item.unitPrice)}
                                      </td>
                                      <td style={{ padding: "7px 8px" }}>
                                        <button
                                          onClick={() =>
                                            startEditDraftItem(item)
                                          }
                                          style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            color: "#9ca3af",
                                          }}
                                          title="Edit"
                                        >
                                          <EditIcon sx={{ fontSize: 16 }} />
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                }),
                            )}
                          </tbody>
                        </table>
                      )}

                      {items.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            padding: "8px 16px",
                            borderTop: "1px solid #e5e7eb",
                            fontSize: 13,
                          }}
                        >
                          <span
                            style={{
                              color: "#6b7280",
                              fontWeight: 600,
                              marginRight: 8,
                            }}
                          >
                            Total:
                          </span>
                          <span style={{ color: "#111827", fontWeight: 700 }}>
                            $
                            {(
                              items.reduce(
                                (sum, item) =>
                                  sum +
                                  Number(item.quantity) *
                                    Number(item.unitPrice),
                                0,
                              ) / 100
                            ).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Custom item form */}
                      {isAddingItem && (
                        <div className="add-item-form">
                          <div className="add-item-form-row">
                            <div className="form-group" style={{ width: 130 }}>
                              <label>
                                Section{" "}
                                <span style={{ color: "#dc2626" }}>*</span>
                              </label>
                              <select
                                className="input"
                                value={newCustomItemData.section}
                                onChange={(e) =>
                                  setNewCustomItemData({
                                    ...newCustomItemData,
                                    section: e.target.value,
                                  })
                                }
                                disabled={creatingItem}
                              >
                                <option value="fabric">Fabric</option>
                                <option value="labor">Labor</option>
                                <option value="hardware">Hardware</option>
                                <option value="furniture">Furniture</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                            <div className="form-group" style={{ flex: 3 }}>
                              <label>
                                Description{" "}
                                <span style={{ color: "#dc2626" }}>*</span>
                              </label>
                              <input
                                type="text"
                                className="input"
                                placeholder="e.g., Bracket, Trim, Extra lining..."
                                value={newCustomItemData.description}
                                onChange={(e) =>
                                  setNewCustomItemData({
                                    ...newCustomItemData,
                                    description: e.target.value,
                                  })
                                }
                                disabled={creatingItem}
                              />
                            </div>
                            <div className="form-group" style={{ width: 80 }}>
                              <label>Qty</label>
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                className="input"
                                value={newCustomItemData.quantity}
                                onChange={(e) =>
                                  setNewCustomItemData({
                                    ...newCustomItemData,
                                    quantity: e.target.value,
                                  })
                                }
                                disabled={creatingItem}
                              />
                            </div>
                            <div className="form-group" style={{ width: 110 }}>
                              <label>Unit Price ($)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="input"
                                placeholder="0.00"
                                value={newCustomItemData.unitPrice}
                                onChange={(e) =>
                                  setNewCustomItemData({
                                    ...newCustomItemData,
                                    unitPrice: e.target.value,
                                  })
                                }
                                disabled={creatingItem}
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label>
                              Notes{" "}
                              <span
                                style={{ color: "#9ca3af", fontWeight: 400 }}
                              >
                                (optional)
                              </span>
                            </label>
                            <input
                              type="text"
                              className="input"
                              placeholder="Optional note"
                              value={newCustomItemData.notes}
                              onChange={(e) =>
                                setNewCustomItemData({
                                  ...newCustomItemData,
                                  notes: e.target.value,
                                })
                              }
                              disabled={creatingItem}
                            />
                          </div>
                          <button
                            onClick={() => handleAddCustomItem(obj.id)}
                            className="save-button"
                            disabled={creatingItem}
                          >
                            {creatingItem ? "Adding..." : "Add Item"}
                          </button>
                        </div>
                      )}

                      {items.length === 0 && !isAddingItem && (
                        <div
                          style={{
                            padding: "12px 16px",
                            fontSize: 13,
                            color: "#9ca3af",
                          }}
                        >
                          No items — SOP auto-calc skipped for this type. Click
                          "Add Custom Item" to add manually.
                        </div>
                      )}

                      {/* Photo gallery */}
                      {(() => {
                        const activeImages =
                          obj.draftQuoteObjectImagesByDraftQuoteObjectId.nodes.filter(
                            (img) => !img.archivedAt,
                          );
                        const isUploading = uploadingObjectId === obj.id;
                        return (
                          <div
                            style={{
                              padding: "12px 16px",
                              borderTop: "1px solid #f3f4f6",
                            }}
                          >
                            {activeImages.length > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: 10,
                                  flexWrap: "wrap",
                                  marginBottom: 10,
                                }}
                              >
                                {activeImages.map((img) => (
                                  <div
                                    key={img.id}
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 3,
                                    }}
                                  >
                                    <img
                                      src={`${SERVER_URL}/uploads/object-images/${img.storedFilename}`}
                                      alt={img.originalFilename}
                                      title={img.originalFilename}
                                      style={{
                                        width: 90,
                                        height: 90,
                                        objectFit: "cover",
                                        borderRadius: 4,
                                        border: "1px solid #e5e7eb",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        window.open(
                                          `${SERVER_URL}/uploads/object-images/${img.storedFilename}`,
                                          "_blank",
                                        )
                                      }
                                    />
                                    <button
                                      onClick={() => handleArchiveImage(img.id)}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 3,
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#9ca3af",
                                        fontSize: 11,
                                        padding: "1px 4px",
                                      }}
                                      title={`Remove ${img.originalFilename}`}
                                    >
                                      <DeleteIcon sx={{ fontSize: 13 }} />
                                      Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {isUploading ? (
                              <span style={{ fontSize: 13, color: "#6b7280" }}>
                                Uploading...
                              </span>
                            ) : (
                              <button
                                className="add-item-button btn-icon"
                                style={{ fontSize: 12 }}
                                onClick={() => {
                                  uploadTargetRef.current = obj.id;
                                  fileInputRef.current?.click();
                                }}
                              >
                                <AddPhotoAlternateIcon sx={{ fontSize: 15 }} />{" "}
                                Add Photo
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })
              )}

              {/* "+ Add Object" lives below the list once there's at least one
              object, next to where a new card will actually land — hidden
              while an in-place edit is open so only one form is ever open
              at a time (see handleGoBack). */}
              {objects.length > 0 && editingObjectId == null && (
                <div style={{ marginTop: 16 }}>
                  <button
                    className="add-button btn-icon"
                    onClick={() => {
                      setShowAddObjectForm(!showAddObjectForm);
                      setNewObjectData(blankObjectForm);
                      setTypeFormInitialValues(null);
                      setFormInstanceKey((k) => k + 1);
                      setError(null);
                    }}
                  >
                    {showAddObjectForm ? (
                      <CloseIcon fontSize="small" />
                    ) : (
                      <AddIcon fontSize="small" />
                    )}
                    {showAddObjectForm ? "Close" : "Add Object"}
                  </button>
                  {showAddObjectForm && (
                    <div style={{ marginTop: 16 }}>{renderObjectForm()}</div>
                  )}
                </div>
              )}

              {objects.length > 0 &&
                finalizationPreview &&
                (finalizationPreview.measureChargeApplies ||
                  finalizationPreview.remoteApplies) && (
                  <div
                    style={{
                      padding: "12px 16px",
                      backgroundColor: "#fef3c7",
                      color: "#92400e",
                      borderRadius: 6,
                      marginTop: 16,
                      fontSize: 14,
                    }}
                  >
                    <strong>
                      {alreadyFinalized
                        ? "The finalized quote includes (update to apply current draft changes):"
                        : "Finalizing will add:"}
                    </strong>
                    <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                      {finalizationPreview.measureChargeApplies && (
                        <li>
                          Measure Charge — $
                          {(
                            finalizationPreview.measureChargeCents / 100
                          ).toFixed(2)}
                        </li>
                      )}
                      {finalizationPreview.remoteApplies && (
                        <li>
                          {finalizationPreview.remoteDescription} — $
                          {(finalizationPreview.remoteCents / 100).toFixed(2)} (
                          {finalizationPreview.motorCount} motorized shade
                          {finalizationPreview.motorCount === 1 ? "" : "s"})
                        </li>
                      )}
                    </ul>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
